// Frontend Sentry initialization and helpers.
//
// Sentry is only initialized if REACT_APP_SENTRY_DSN is set at build time. In
// dev/test or when no DSN is configured the helpers fall back to no-ops so the
// app continues to behave normally.

import * as Sentry from '@sentry/react';

let _initialized = false;
let _enabled = false;

export function initSentry() {
    if (_initialized) return _enabled;
    _initialized = true;

    const dsn = process.env.REACT_APP_SENTRY_DSN;
    if (!dsn) {
        _enabled = false;
        return _enabled;
    }

    try {
        Sentry.init({
            dsn,
            environment:
                process.env.REACT_APP_ENVIRONMENT ||
                process.env.NODE_ENV ||
                'development',
            release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
            tracesSampleRate: Number(
                process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0
            ),
        });
        _enabled = true;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize Sentry:', err);
        _enabled = false;
    }
    return _enabled;
}

/**
 * Capture a thrown error with structured context. Safe to call even when
 * Sentry is not initialized (becomes a no-op).
 */
export function captureException(error, opts = {}) {
    if (!initSentry()) return;
    try {
        Sentry.withScope((scope) => {
            if (opts.tags) scope.setTags(opts.tags);
            if (opts.extra) scope.setExtras(opts.extra);
            if (opts.level) scope.setLevel(opts.level);
            Sentry.captureException(error);
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Sentry captureException failed:', e);
    }
}

/**
 * Capture a structured message (typically used for non-throw error paths
 * like a non-2xx fetch response).
 */
export function captureMessage(message, opts = {}) {
    if (!initSentry()) return;
    try {
        Sentry.withScope((scope) => {
            if (opts.tags) scope.setTags(opts.tags);
            if (opts.extra) scope.setExtras(opts.extra);
            scope.setLevel(opts.level || 'info');
            Sentry.captureMessage(message);
        });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Sentry captureMessage failed:', e);
    }
}

export function addBreadcrumb(breadcrumb) {
    if (!initSentry()) return;
    try {
        Sentry.addBreadcrumb(breadcrumb);
    } catch (e) {
        // ignore
    }
}

export { Sentry };
